export const FLARE_ANGLE_DEG = 0;
export const SURFACE_INSET = 0.05;
/** Nudge form plane slightly below the theoretical slice so it aligns with the form cylinder opening (contour detection plane). */
export const FORM_PLANE_NUDGE_BELOW = 0.02;
/** Y offset of the engulf cylinder/frustum relative to the adapter position (world units = cm). */
export const ENGULF_Y_OFFSET = 0.1;

/**
 * Curvature of the socket ↔ frustum transition loft, normalized to [0, 1].
 *   0  = a straight (linear) transition, no curve at the socket or the rim.
 *   1  = the maximum blend (fullest curve).
 * The value scales the loft's Bezier handle length in buildSmoothLoftStrip
 * (see LOFT_CURVATURE_MAX_HANDLE in printableLocking.js). This is now a fixed
 * value — the tool cards no longer expose a "Curvature" slider.
 */
export const LOFT_CURVATURE_MIN = 0;
export const LOFT_CURVATURE_MAX = 1;
export const LOFT_CURVATURE_DEFAULT = 0.7;

/**
 * Position of the loft's main bend, as a fraction from the TOP (socket) end
 * toward the BOTTOM (frustum) end, normalized to [0, 1].
 *   0.5 = the bend sits at the middle (both Bezier handles equal — the historic
 *         symmetric shape: curve in the centre, near-straight run into the frustum).
 *   < 0.5 = the bend moves UP toward the socket (short socket-side handle).
 *   > 0.5 = the bend moves DOWN toward the frustum (short frustum-side handle).
 * Only meaningful when LOFT_CURVATURE > 0 (a straight loft has no bend to place).
 */
export const LOFT_CURVE_POSITION_MIN = 0;
export const LOFT_CURVE_POSITION_MAX = 1;
export const LOFT_CURVE_POSITION_DEFAULT = 0.7;

/**
 * INWARD GUARD-RAIL — how far the transition loft may bow RADIALLY INWARD past
 * the straight socket→frustum chord (the line drawn as the upward "connection
 * rays" in the locking preview). The loft may bulge outward freely; excessive
 * inward penetration is pulled back out so the printed wall cannot undercut the
 * socket / go paper-thin.
 *
 * The limit is `max(LOFT_MAX_INWARD_OFFSET, chord * LOFT_MAX_INWARD_CHORD_FRACTION)`
 * per fiber: an absolute floor (cm) for very short fibers, and a scale-free
 * fraction of the fiber's own chord for everything else. A purely absolute limit
 * was far tighter than the dip a legitimate curvature/curve-position Bezier
 * makes (a ~3 cm fiber naturally bows ~0.1–0.3 cm inward), so it fought the
 * sliders instead of guarding against pathological fibers — which is all it is
 * for. Sized so that every in-range slider combination passes untouched and the
 * built loft matches the preview, while a fiber that would genuinely fold inside
 * the socket is still tamed.
 *
 * The guard SATURATES SMOOTHLY (tanh) rather than clamping: shallow dips pass
 * through essentially untouched (the correction grows with the CUBE of the dip),
 * deep ones ease asymptotically toward the limit, and the map is smooth
 * everywhere — no knee, no threshold, so no row can meet its neighbour at an
 * angle. The old hard clamp did exactly that: it left the shallow rows alone and
 * yanked the deeper ones out, welding the two bands together at a tangent break —
 * the visible crease/line at the curve position. Applied in applyLoftInwardGuard
 * (printableLocking.js), shared by the built loft and the preview surface.
 */
export const LOFT_MAX_INWARD_OFFSET = 0.05;
export const LOFT_MAX_INWARD_CHORD_FRACTION = 0.18;
